import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bot, Send, UserCircle2 } from "lucide-react";

export default function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLeadId = searchParams.get("lead") || "";
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState(initialLeadId);
  const [lead, setLead] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState({ name: "", email: "" });
  const scrollRef = useRef(null);

  const loadLeads = () => api.get("/leads").then((r) => setLeads(r.data));

  useEffect(() => { loadLeads(); }, []);

  useEffect(() => {
    if (leadId) {
      api.get(`/leads/${leadId}`).then((r) => setLead(r.data));
    } else {
      setLead(null);
    }
  }, [leadId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lead?.conversation?.length]);

  const startNew = async () => {
    if (!creating.name || !creating.email) return toast.error("Nombre y email son obligatorios");
    const r = await api.post("/leads", { ...creating, source: "bot" });
    setLeadId(r.data.id);
    setSearchParams({ lead: r.data.id });
    setCreating({ name: "", email: "" });
    loadLeads();
  };

  const send = async () => {
    if (!leadId || !msg.trim()) return;
    const userMsg = msg;
    setMsg("");
    setSending(true);
    // Optimistic
    setLead((p) => ({
      ...p,
      conversation: [...(p?.conversation || []), { role: "user", content: userMsg, ts: new Date().toISOString() }],
    }));
    try {
      const r = await api.post(`/leads/${leadId}/chat`, { message: userMsg });
      setLead((p) => ({
        ...p,
        conversation: [...p.conversation, { role: "assistant", content: r.data.reply, ts: new Date().toISOString() }],
        score: r.data.score,
        summary: r.data.summary,
        status: r.data.status,
      }));
      loadLeads();
    } catch {
      toast.error("Error en el chat");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="stagger">
      <PageHeader
        overline="Bot Calificador"
        title="Maya, tu asistente de pre-venta."
        subtitle="Conversa con leads para entender su intención y obtener un score de calidad antes de pasar al agente humano."
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lead selector */}
        <Card className="edit-card p-5 rounded-sm shadow-none lg:col-span-1">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Conversación con</p>
          <Select value={leadId} onValueChange={(v) => { setLeadId(v); setSearchParams({ lead: v }); }}>
            <SelectTrigger className="rounded-sm bg-white border-[#E2DCD0]" data-testid="chat-lead-select"><SelectValue placeholder="Selecciona un lead" /></SelectTrigger>
            <SelectContent>
              {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.email}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="mt-6 pt-6 border-t border-[#E2DCD0]">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">O crea uno nuevo</p>
            <div className="space-y-2">
              <Input data-testid="chat-new-name" placeholder="Nombre" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} className="rounded-sm bg-white border-[#E2DCD0]" />
              <Input data-testid="chat-new-email" type="email" placeholder="Email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} className="rounded-sm bg-white border-[#E2DCD0]" />
              <Button data-testid="chat-new-button" onClick={startNew} variant="outline" className="w-full rounded-sm border-[#D95D39] text-[#D95D39] hover:bg-[#D95D39] hover:text-white">
                Iniciar conversación
              </Button>
            </div>
          </div>

          {lead && (
            <div className="mt-6 pt-6 border-t border-[#E2DCD0] space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">Score IA</span>
                <Badge variant="outline" className="rounded-sm">{lead.score}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">Estado</span>
                <Badge variant="outline" className="rounded-sm">{lead.status}</Badge>
              </div>
              {lead.summary && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-1">Resumen IA</p>
                  <p className="text-xs text-[#5C616A] italic">{lead.summary}</p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Chat area */}
        <Card className="edit-card rounded-sm shadow-none lg:col-span-2 flex flex-col h-[600px]">
          <div className="px-5 py-4 border-b border-[#E2DCD0] flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#0A1128] flex items-center justify-center">
              <Bot className="w-4 h-4 text-[#D95D39]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-display text-lg text-[#0A1128] leading-none">Maya</p>
              <p className="text-xs text-[#8A8F98]">Asistente calificador · Claude Sonnet 4.5</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            {!lead && (
              <div className="text-center text-[#8A8F98] text-sm pt-20">
                Selecciona o crea un lead para comenzar la conversación.
              </div>
            )}
            {lead && lead.conversation?.length === 0 && (
              <div className="text-center text-[#8A8F98] text-sm pt-20">
                Empieza diciendo "Hola" para que Maya inicie la conversación de calificación.
              </div>
            )}
            {lead?.conversation?.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-[#D95D39]" : "bg-[#0A1128]"}`}>
                  {m.role === "user" ? <UserCircle2 className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                </div>
                <div className={`max-w-[75%] px-4 py-3 rounded-sm text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#0A1128] text-white" : "bg-[#F9F8F6] border border-[#E2DCD0] text-[#1A1F2B]"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-sm bg-[#0A1128] flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
                <div className="px-4 py-3 bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm text-sm text-[#8A8F98]">Maya está escribiendo...</div>
              </div>
            )}
          </div>

          <div className="border-t border-[#E2DCD0] p-3 flex gap-2">
            <Input
              data-testid="chat-input"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={lead ? "Escribe como si fueras el cliente..." : "Selecciona un lead primero"}
              disabled={!lead || sending}
              className="rounded-sm bg-white border-[#E2DCD0]"
            />
            <Button data-testid="chat-send" onClick={send} disabled={!lead || sending || !msg.trim()} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
